import axios from 'axios';
import { getAuthToken, refreshAuthToken, setAuthToken, clearAuthData } from '@/utilsAuth';
import NProgress from 'nprogress';
import { getSnotify } from '@/plugins/snotify-adapter';
import router from '@/router';

import 'nprogress/nprogress.css';

const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      NProgress.start();
    }
    return config;
  },
  (error) => {
    NProgress.done();
  
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    NProgress.done();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

      if (error.response?.status === 400) {
        let msg = 'Une erreur est survenue.';

        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            const json = JSON.parse(text);
            msg = json.message || msg;
          } catch (e) {
            // leave default msg
          }
        } else {
          msg = error.response.data?.message || msg;
        }

        getSnotify()?.error(msg);
      }


    if (error.response?.status === 401 && !originalRequest._retry) {

      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const newToken = await refreshAuthToken();
          if (!newToken) {
            throw new Error('Refresh token failed');
          }
          setAuthToken(newToken);
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          await clearAuthData();
          processQueue(refreshError, null);
          if (router.currentRoute.value.path !== '/login') {
            router.replace('/login').catch(() => {});
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
    }

    if (error.response?.status === 403) {
      let msg = "Vous n'êtes pas autorisé à effectuer cette action.";

      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          msg = json.message || msg;
        } catch (e) {
          if (text) msg = text;
        }
      } else {
        msg = error.response.data?.message || msg;
      }

      getSnotify()?.error(msg);
    }

    if (error.response?.status === 500) {
      if (router.currentRoute.value.path !== '/error-500') {
        const currentPath = router.currentRoute.value.fullPath;
        router.replace({
          path: '/error-500',
          query: { from: currentPath }
        }).catch(() => {});
      }
    }

    NProgress.done();
    return Promise.reject(error);
  }
);

export default apiClient;
